import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  Delete,
  Upload,
  Visibility,
  Logout,
  Dashboard as DashboardIcon,
  CloudUpload,
  Storage,
  Settings,
  CheckCircle,
  Error
} from '@mui/icons-material';
import axios from 'axios';
import Login from './Login';
import { WebSocketProvider, useWebSocketContext } from './context/WebSocketContext';
import Dashboard from './components/Dashboard';
import DeploymentHistory from './components/DeploymentHistory';
import LogViewer from './components/LogViewer';
import SettingsPanel from './components/SettingsPanel';
import DeploymentConfigs from './components/DeploymentConfigs';

// Use relative path in production, absolute URL in development
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

function AppContent() {
  const [tabValue, setTabValue] = useState(0);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [serverDialogOpen, setServerDialogOpen] = useState(false);
  const [serverForm, setServerForm] = useState({
    name: '',
    script: '',
    cwd: '',
    args: ''
  });
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState(null);
  const [initialLogs, setInitialLogs] = useState('');
  const [user, setUser] = useState(null);

  const { connected, subscribe } = useWebSocketContext();

  useEffect(() => {
    // Get user from session
    axios.get(`${API_BASE_URL}/api/auth/check`, { withCredentials: true })
      .then(res => {
        if (res.data.authenticated) {
          setUser(res.data.user);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Reduced polling with WebSocket
    return () => clearInterval(interval);
  }, []);

  // Subscribe to real-time status updates
  useEffect(() => {
    const unsubscribe = subscribe('server_event', () => {
      fetchStatus();
    });
    return unsubscribe;
  }, [subscribe]);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/status`, {
        withCredentials: true
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };


  const handleServerAction = async (action, serverName) => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/server/${action}`, {
        name: serverName
      }, {
        withCredentials: true
      });

      setMessage({ type: 'success', text: response.data.message });
      fetchStatus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || `${action} failed`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartServer = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/server/start`, {
        ...serverForm,
        args: serverForm.args ? serverForm.args.split(' ') : []
      }, {
        withCredentials: true
      });

      setMessage({ type: 'success', text: response.data.message });
      setServerDialogOpen(false);
      setServerForm({ name: '', script: '', cwd: '', args: '' });
      fetchStatus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to start server'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogs = async (serverName) => {
    setSelectedServer(serverName);
    setInitialLogs('Loading...');
    setLogsDialogOpen(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/server/logs/${serverName}?lines=200`, {
        withCredentials: true
      });
      setInitialLogs(response.data.logs || 'No logs available');
    } catch (error) {
      setInitialLogs(`Error fetching logs: ${error.response?.data?.error || error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'success';
      case 'stopped': return 'error';
      case 'errored': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box 
        sx={{ 
          mb: 4, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 3,
          borderBottom: '2px solid',
          borderColor: 'divider'
        }}
      >
        <Box>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            TGC Infrastructure Admin
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Deploy and manage your infrastructure
            </Typography>
            <Chip
              icon={connected ? <CheckCircle /> : <Error />}
              label={connected ? 'Connected' : 'Disconnected'}
              color={connected ? 'success' : 'error'}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={user?.username}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Button
            variant="outlined"
            startIcon={<Logout />}
            onClick={async () => {
              await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
              window.location.reload();
            }}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {message.text && (
        <Alert
          severity={message.type}
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <Paper 
        sx={{ 
          mb: 4,
          borderRadius: 3,
          boxShadow: 2,
          overflow: 'hidden'
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 64
            }
          }}
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
          <Tab icon={<CloudUpload />} label="Deployments" iconPosition="start" />
          <Tab icon={<Storage />} label="API Servers" iconPosition="start" />
          <Tab icon={<Settings />} label="Settings" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Dashboard Tab */}
      {tabValue === 0 && (
        <Dashboard status={status} />
      )}

      {/* Deployments Tab */}
      {tabValue === 1 && (
        <Box>
          <DeploymentConfigs onMessage={setMessage} />
          <Box sx={{ mt: 3 }}>
            <DeploymentHistory onMessage={setMessage} />
          </Box>
        </Box>
      )}

      {/* API Servers Tab */}
      {tabValue === 2 && (
        <Box>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">
                API Servers
              </Typography>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={() => setServerDialogOpen(true)}
              >
                Start New Server
              </Button>
            </Box>

            {status?.apiProcesses && status.apiProcesses.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>PID</TableCell>
                      <TableCell>Uptime</TableCell>
                      <TableCell>Restarts</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {status.apiProcesses.map((process) => (
                      <TableRow key={process.name}>
                        <TableCell>{process.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={process.status}
                            color={getStatusColor(process.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{process.pid || 'N/A'}</TableCell>
                        <TableCell>
                          {process.uptime
                            ? `${Math.floor((Date.now() - process.uptime) / 1000 / 60)}m`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{process.restarts || 0}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleViewLogs(process.name)}
                            title="View Logs"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleServerAction('restart', process.name)}
                            title="Restart"
                            disabled={loading}
                          >
                            <Refresh fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleServerAction('stop', process.name)}
                            title="Stop"
                            disabled={loading}
                          >
                            <Stop fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleServerAction('delete', process.name)}
                            title="Delete"
                            disabled={loading}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No API servers running. Start a new server to begin.
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* Settings Tab */}
      {tabValue === 3 && (
        <SettingsPanel user={user} />
      )}

      {/* Start Server Dialog */}
      <Dialog open={serverDialogOpen} onClose={() => setServerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start New API Server</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Server Name"
            value={serverForm.name}
            onChange={(e) => setServerForm({ ...serverForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Script Path"
            value={serverForm.script}
            onChange={(e) => setServerForm({ ...serverForm, script: e.target.value })}
            margin="normal"
            required
            placeholder="/path/to/server.js"
            helperText="Full path to the Node.js script"
          />
          <TextField
            fullWidth
            label="Working Directory (optional)"
            value={serverForm.cwd}
            onChange={(e) => setServerForm({ ...serverForm, cwd: e.target.value })}
            margin="normal"
            placeholder="/path/to/project"
          />
          <TextField
            fullWidth
            label="Arguments (optional)"
            value={serverForm.args}
            onChange={(e) => setServerForm({ ...serverForm, args: e.target.value })}
            margin="normal"
            placeholder="--port 3000 --env production"
            helperText="Space-separated arguments"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleStartServer}
            variant="contained"
            disabled={loading || !serverForm.name || !serverForm.script}
          >
            Start Server
          </Button>
        </DialogActions>
      </Dialog>


      {/* Log Viewer */}
      <LogViewer
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        serverName={selectedServer}
        initialLogs={initialLogs}
      />
    </Container>
  );
}

function DeployForm({ type, onDeploy, loading }) {
  const [repoPath, setRepoPath] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onDeploy(type, repoPath);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 2 }}>
        <TextField
          label="Repository"
          value={repoPath}
          onChange={(e) => setRepoPath(e.target.value)}
          fullWidth
          placeholder="/path/to/repository"
          helperText="Full path to the git repository"
          required
          size="small"
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
          disabled={loading || !repoPath}
          sx={{ minWidth: 120, height: 40 }}
        >
          {loading ? 'Deploying...' : 'Deploy'}
        </Button>
      </Box>
    </form>
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/check`, {
        withCredentials: true
      });
      if (response.data.authenticated) {
        setAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogin = () => {
    setAuthenticated(true);
  };

  if (checkingAuth) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!authenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <WebSocketProvider authenticated={authenticated}>
      <AppContent />
    </WebSocketProvider>
  );
}

export default App;
